import { TIER_LABELS } from '@/constants/features';
import { useFeatureAccess } from '@/hooks/use-feature-access.hook';
import { useOpenUpgrade } from '@/hooks/use-open-upgrade.hook';
import { CARD_TRIAL_DAYS, TRIAL_ENDING_SOON_DAYS } from '@/lib/card-trial';
import { toUpgradeTier, type UpgradeTier } from '@/lib/upgrade-link';
import {
  useGetPlansQuery,
  useLazyGetPlansQuery,
} from '@/store/features/billing.feature';
import type { FeatureAccess, TrialState } from '@/types';
import { useRouterState } from '@tanstack/react-router';
import { useCallback, useState } from 'react';

const DAY_MS = 86_400_000;

/** Which workspaces the sidebar's Upgrade button speaks to. */
export type UpgradeCtaKind = 'free' | 'starter' | 'trial';

export interface UpgradeCtaTrial {
  /** Whole days left, counted as the trial bar counts them. */
  daysLeft: number;
  /** Whole days used, 0 to `days`. */
  daysUsed: number;
  /** The trial's length in days. */
  days: number;
  /** In its last days, when the countdown turns amber. */
  endingSoon: boolean;
}

/** What the sidebar's Upgrade button shows, and where it sends people. */
export interface UpgradeCta {
  kind: UpgradeCtaKind;
  /** The status line's lead: "Free plan", "Pro trial", "Starter plan". */
  title: string;
  /** After the lead: "50 credits a month", "9 days left"; null for none. */
  detail: string | null;
  /** The button's label. */
  label: string;
  /** The folded rail's tooltip and accessible name. */
  tooltip: string;
  /** The plan the wizard opens on; undefined lets the wizard pick. */
  plan?: UpgradeTier;
  trial: UpgradeCtaTrial | null;
}

type CtaTrial = Pick<
  TrialState,
  'active' | 'days_remaining' | 'plan' | 'started_at' | 'ends_at'
>;

export interface UpgradeCtaInput {
  /**
   * The entitlements are in for an organization. A failed background refetch
   * keeps the last ones, so it doesn't count against this.
   */
  ready: boolean;
  tierCategory: FeatureAccess['tier_category'];
  isAppSumoOnly: boolean;
  trial: CtaTrial | null;
  /** The Free plan's monthly grant from the catalog, when it is known. */
  freeMonthlyCredits: number | null;
  /** The time to count a trial's days left from, in epoch milliseconds. */
  now: number;
}

const planLabel = (plan: string) =>
  TIER_LABELS[plan] ?? `${plan.charAt(0).toUpperCase()}${plan.slice(1)}`;

const daysLeftText = (days: number) =>
  days === 0 ? 'ends today' : `${days} day${days === 1 ? '' : 's'} left`;

/**
 * Whole days left on a trial, as the trial bar counts them: up from
 * `ends_at`, so any time still on the clock reads as a day; once that has
 * passed or is missing, the server's own count; never NaN.
 */
function trialDaysLeft(trial: CtaTrial, now: number): number {
  const ms = (trial.ends_at ? Date.parse(trial.ends_at) : NaN) - now;
  if (ms > 0) return Math.ceil(ms / DAY_MS);
  return Number.isFinite(trial.days_remaining)
    ? Math.max(0, Math.round(trial.days_remaining))
    : 0;
}

/** The trial's length in days: its own dates, else the standard 14. */
function trialLength(trial: CtaTrial): number {
  const start = trial.started_at ? Date.parse(trial.started_at) : NaN;
  const end = trial.ends_at ? Date.parse(trial.ends_at) : NaN;
  const days = Math.round((end - start) / DAY_MS);
  return Number.isFinite(days) && days > 0 ? days : CARD_TRIAL_DAYS;
}

/**
 * Whether the sidebar shows its Upgrade button, and what it says. Only once
 * the entitlements are in, for a Free or Starter workspace or one on an active trial; never for Pro, Scale or Enterprise,
 * or for an AppSumo-only workspace (its upgrades are AppSumo codes). It stays
 * on `/upgrade` too. A trial wins over the tier, which reads as the trial's
 * plan while it runs.
 */
export function deriveUpgradeCta(input: UpgradeCtaInput): UpgradeCta | null {
  if (!input.ready || input.isAppSumoOnly) return null;

  const trial = input.trial;
  if (trial?.active) {
    const daysLeft = trialDaysLeft(trial, input.now);
    const days = trialLength(trial);
    const title = `${planLabel(trial.plan)} trial`;
    const detail = daysLeftText(daysLeft);
    return {
      kind: 'trial',
      title,
      detail,
      label: 'Upgrade',
      tooltip: `${title} · ${detail} — Upgrade`,
      plan: toUpgradeTier(trial.plan),
      trial: {
        daysLeft,
        daysUsed: Math.max(0, days - daysLeft),
        days,
        endingSoon: daysLeft <= TRIAL_ENDING_SOON_DAYS,
      },
    };
  }
  if (input.tierCategory === 'free') {
    const credits = input.freeMonthlyCredits;
    return {
      kind: 'free',
      title: 'Free plan',
      detail:
        credits !== null && credits > 0
          ? `${credits.toLocaleString('en-US')} credits a month`
          : null,
      label: 'Upgrade',
      tooltip: 'Upgrade',
      trial: null,
    };
  }
  if (input.tierCategory === 'starter') {
    return {
      kind: 'starter',
      title: 'Starter plan',
      detail: null,
      label: 'Upgrade to Pro',
      tooltip: 'Upgrade to Pro',
      plan: 'pro',
      trial: null,
    };
  }
  return null;
}

/** The longest the button waits for the plans before opening anyway. */
const PLANS_WAIT_MS = 4000;

/**
 * Opens the upgrade wizard (or /plans) once the plans are loaded, so the page
 * opens ready rather than as a skeleton; cached plans open it at once.
 * `opening` is true from the click until that page is on screen.
 */
export function useOpenUpgradeReady() {
  const { organizationId } = useFeatureAccess();
  const openUpgrade = useOpenUpgrade();
  const [loadPlans] = useLazyGetPlansQuery();
  const [loading, setLoading] = useState(false);
  const routing = useRouterState({
    select: (state) =>
      state.status === 'pending' &&
      /^\/(upgrade|plans)(\/|$)/.test(state.location.pathname),
  });
  const open = useCallback(
    async (target: Parameters<typeof openUpgrade>[0]) => {
      setLoading(true);
      try {
        if (organizationId) {
          await Promise.race([
            loadPlans({ organizationId }, true)
              .unwrap()
              .catch(() => undefined),
            new Promise((resolve) => window.setTimeout(resolve, PLANS_WAIT_MS)),
          ]);
        }
      } finally {
        setLoading(false);
      }
      openUpgrade(target);
    },
    [organizationId, loadPlans, openUpgrade]
  );
  return { open, opening: loading || routing };
}

/** The sidebar's Upgrade button for this workspace, or null for none. */
export function useUpgradeCta(): UpgradeCta | null {
  const { organizationId, isReady, tierCategory, isAppSumoOnly, trial } =
    useFeatureAccess();
  // Not `!error`: a failed background refetch keeps the last entitlements,
  // and the button with them.
  const ready = isReady && organizationId !== '';
  // Only the Free button reads the catalog (for Free's monthly grant); the
  // credits menu has usually fetched it already.
  const wantsCatalog =
    ready && tierCategory === 'free' && trial?.active !== true;
  const { currentData: catalog } = useGetPlansQuery(
    { organizationId },
    { skip: !wantsCatalog }
  );
  const freeMonthlyCredits =
    catalog?.plans.find((plan) => plan.tier_key === 'free')?.monthly_credits ??
    null;
  // Not memoised: a trial's days left count from the clock, so every render
  // (a route change, the entitlements' polling) reads them afresh.
  return deriveUpgradeCta({
    ready,
    tierCategory,
    isAppSumoOnly,
    trial,
    freeMonthlyCredits,
    now: Date.now(),
  });
}
